import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ChatCircleDots } from "@phosphor-icons/react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { DiscussionPanel } from "@/components/discussion/discussion-panel";
import { DashboardSectionsProvider } from "@/context/dashboard-sections";
import { useCommentCount } from "@/hooks/use-comments";
import { useAuthenticationStore } from "@/store/authentication";
import { cn } from "@/lib/utils";
import { CommenterIdentity } from "@/schema/comments";

export const Route = createFileRoute("/dashboards/$dashboardId")({
  component: OwnerDashboardPage,
  validateSearch: (search: Record<string, unknown>) => ({
    shareToken: typeof search.shareToken === "string" ? search.shareToken : undefined,
  }),
});

function OwnerDashboardPage() {
  const { dashboardId } = Route.useParams();
  const { shareToken } = Route.useSearch();
  const [discussionOpen, setDiscussionOpen] = useState(false);

  const user = useAuthenticationStore((s) => s.user);
  const organization = useAuthenticationStore((s) => s.organization);

  const ownerScope = useMemo(() => {
    if (!user || !organization) return undefined;
    return { user_id: user.id, organization_id: organization.id };
  }, [user, organization]);

  const ownerIdentity = useMemo<CommenterIdentity | undefined>(() => {
    if (!user) return undefined;
    return {
      email: user.email,
      display_name: user.name || undefined,
    };
  }, [user]);

  const PANEL_WIDTH_PX = 400;

  return (
    <DashboardSectionsProvider>
      <div
        className={cn("flex flex-col h-[100dvh] w-screen transition-[padding] duration-200")}
        style={discussionOpen ? { paddingRight: PANEL_WIDTH_PX } : undefined}
      >
        <header className="flex items-center justify-between px-4 h-12 border-b shrink-0 bg-background">
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-sm font-semibold truncate">Dashboard {dashboardId.slice(0, 8)}</h1>
            {!ownerScope ? <span className="text-[11px] text-muted-foreground">(sign in to enable owner controls)</span> : null}
          </div>
          <DiscussionToggle dashboardId={dashboardId} shareToken={shareToken} ownerScope={ownerScope} open={discussionOpen} onToggle={() => setDiscussionOpen((v) => !v)} />
        </header>

        <div className="flex-1 grid place-items-center text-sm text-muted-foreground">
          {/* Real owner-side dashboard rendering happens elsewhere; this route demonstrates the panel wiring. */}
          Render the owner's saved dashboard view here.
        </div>
      </div>

      {ownerScope ? (
        <DiscussionPanel
          mode="owner"
          dashboardId={dashboardId}
          ownerScope={ownerScope}
          ownerIdentity={ownerIdentity}
          shareToken={shareToken}
          open={discussionOpen}
          onOpenChange={setDiscussionOpen}
        />
      ) : shareToken ? (
        <DiscussionPanel
          mode="share"
          shareToken={shareToken}
          open={discussionOpen}
          onOpenChange={setDiscussionOpen}
        />
      ) : null}
    </DashboardSectionsProvider>
  );
}

function DiscussionToggle({
  dashboardId,
  shareToken,
  ownerScope,
  open,
  onToggle,
}: {
  dashboardId: string;
  shareToken?: string;
  ownerScope?: { user_id: string; organization_id: string };
  open: boolean;
  onToggle: () => void;
}) {
  const count = useCommentCount({
    mode: ownerScope ? "owner" : "share",
    dashboardId: ownerScope ? dashboardId : undefined,
    scope: ownerScope,
    shareToken: ownerScope ? undefined : shareToken,
    enabled: !!ownerScope || !!shareToken,
    refetchIntervalMs: open ? 0 : 30_000,
  });

  const total = count.data?.total ?? 0;
  const unresolved = count.data?.unresolved ?? 0;

  return (
    <Button type="button" size="sm" variant={open ? "secondary" : "outline"} className="h-8 gap-1.5" onClick={onToggle}>
      <ChatCircleDots weight={open ? "fill" : "regular"} className="size-4" />
      <span className="text-xs">Discussion</span>
      {total > 0 ? <Badge variant="secondary" className="h-4 px-1 text-[10px]">{total}</Badge> : null}
      {unresolved > 0 ? (
        <Badge variant="outline" className="h-4 px-1 text-[10px] border-amber-500/40 text-amber-700 dark:text-amber-300 bg-amber-500/10">
          {unresolved} open
        </Badge>
      ) : null}
    </Button>
  );
}
